import { isValidElement, useMemo, type ComponentProps, type JSX, type ReactNode } from "react";
import { AlertTriangle, Check, CircleHelp, Database, FileText, Gavel, Globe, Scale, Wrench, X, type LucideIcon } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { MarkdownBody, cn, type CitationLike } from "@ncp/ui";
import type { AssessmentReference } from "./api";
import { AssessmentChart, parseChart } from "./AssessmentChart";

/**
 * The platform's written text, in one type scale: 13 px body, small uppercase section labels, compact tables whose status cells
 * (✅ ⚠️ ❌ ❓) are drawn as icons, quotes as a gold call-out, ```chart blocks as charts, and `[S1]` markers as hover cards on the
 * numbered references, which are listed underneath. Used for the assessment and for the agent's answers about it.
 */
export function AssessmentBody({ markdown, references, onOpenReference, compact = false }: {
  markdown: string; references?: AssessmentReference[] | null; onOpenReference?: (r: AssessmentReference) => void; compact?: boolean;
}) {
  const citations = useMemo<CitationLike[]>(() => (references ?? []).map((r) => ({ id: `ref-${r.n}`, index: r.n, sourceType: r.sourceType, sourceId: r.sourceId, title: r.title, locator: r.locator ?? null, excerpt: r.excerpt ?? null })), [references]);
  const open = (c: CitationLike) => { const r = references?.find((x) => x.n === c.index); if (r) onOpenReference?.(r); };
  return (
    <MarkdownBody
      citations={citations.length ? citations : undefined}
      onOpenCitation={open}
      components={ELEMENTS}
      className={cn(
        "max-w-none font-sans text-[13px] leading-6 text-aeblack-900",
        "[&_h1]:font-sans [&_h2]:font-sans [&_h3]:font-sans [&_h4]:font-sans",
        "[&_h1]:text-[11px] [&_h2]:text-[11px] [&_h3]:text-[11px] [&_h1]:uppercase [&_h2]:uppercase [&_h3]:uppercase [&_h1]:tracking-wider [&_h2]:tracking-wider [&_h3]:tracking-wider",
        "[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:text-seablue-900 [&_h2]:text-seablue-900 [&_h3]:text-seablue-900",
        "[&_h1]:mb-1.5 [&_h2]:mb-1.5 [&_h3]:mb-1.5 [&_h1]:mt-5 [&_h2]:mt-5 [&_h3]:mt-5 [&>h1:first-child]:mt-0 [&>h2:first-child]:mt-0 [&>h3:first-child]:mt-0",
        "[&_h4]:mb-1 [&_h4]:mt-4 [&_h4]:text-[13px] [&_h4]:font-semibold [&_h4]:text-aeblack-950",
        "[&_p]:my-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_li]:my-1 [&_li]:ps-0.5 [&_li::marker]:text-sand-500",
        "[&_strong]:font-semibold [&_strong]:text-aeblack-950 [&_em]:text-sand-800",
        "[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_th]:bg-sand-50 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-[11px] [&_th]:font-semibold [&_th]:text-sand-700 [&_td]:px-2 [&_td]:py-1.5 [&_td]:tabular-nums",
        "[&_blockquote]:my-3 [&_blockquote]:rounded-e-md [&_blockquote]:border-s-[3px] [&_blockquote]:border-aegold-400 [&_blockquote]:bg-aegold-50/70 [&_blockquote]:px-3 [&_blockquote]:py-2 [&_blockquote]:font-normal [&_blockquote]:not-italic [&_blockquote]:text-aeblack-900 [&_blockquote_p]:my-0",
        "[&_hr]:my-4",
        compact && "text-[13px] leading-[1.6rem]",
      )}
    >
      {markdown}
    </MarkdownBody>
  );
}

const REFERENCE_ICON: Record<AssessmentReference["sourceType"], LucideIcon> = { Regulation: Scale, Document: FileText, DataBank: Database, Precedent: Gavel, ComparativeReference: Globe, Tool: Wrench };

/** The numbered sources under an assessment: what each `[S n]` marker in the text points to. */
export function ReferenceList({ references, onOpen }: { references: AssessmentReference[]; onOpen?: (r: AssessmentReference) => void }) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 border-t border-sand-200 pt-3">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:review.references", { count: references.length })}</p>
      <ol className="space-y-1.5">
        {references.map((r) => {
          const Icon = REFERENCE_ICON[r.sourceType] ?? FileText;
          const clickable = !!onOpen && r.sourceType === "Document";
          const body = (
            <>
              <span className="mt-px grid size-5 shrink-0 place-items-center rounded bg-seablue-50 text-[10px] font-semibold tabular-nums text-seablue-900">{r.n}</span>
              <Icon className="mt-0.5 size-3.5 shrink-0 text-sand-600" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className={cn("text-aeblack-900", clickable && "underline-offset-2 group-hover:underline")}>{r.title}</span>
                {r.locator ? <span className="text-sand-600"> · {r.locator}</span> : null}
                {r.excerpt ? <span className="mt-0.5 block text-[11px] italic leading-snug text-sand-600">"{r.excerpt}"</span> : null}
              </span>
            </>
          );
          return (
            <li key={r.n} className="text-xs leading-snug">
              {clickable ? <button type="button" onClick={() => onOpen!(r)} className="group flex w-full items-start gap-2 text-start">{body}</button> : <div className="flex items-start gap-2">{body}</div>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ------------------------------------------------------------------ elements

const STATUS: Record<string, { icon: LucideIcon; tone: string }> = {
  "✅": { icon: Check, tone: "bg-success-50 text-success-700" }, "✓": { icon: Check, tone: "bg-success-50 text-success-700" },
  "⚠️": { icon: AlertTriangle, tone: "bg-warning-50 text-warning-700" }, "⚠": { icon: AlertTriangle, tone: "bg-warning-50 text-warning-700" },
  "❌": { icon: X, tone: "bg-danger-50 text-danger-700" }, "✗": { icon: X, tone: "bg-danger-50 text-danger-700" },
  "❓": { icon: CircleHelp, tone: "bg-sand-100 text-sand-700" },
};
const STATUS_AT_START = /^(✅|✓|⚠️|⚠|❌|✗|❓)\s*/u;

function StatusIcon({ mark }: { mark: string }) {
  const s = STATUS[mark];
  if (!s) return <>{mark}</>;
  const Icon = s.icon;
  return <span className={cn("inline-grid size-[18px] shrink-0 place-items-center rounded-full align-[-4px]", s.tone)}><Icon className="size-3" strokeWidth={2.5} /></span>;
}

/** A cell or list item that starts with a status mark: the mark becomes an icon, the rest of the text follows it. */
function withStatus(children: ReactNode): ReactNode {
  const items = Array.isArray(children) ? children : [children];
  const first = items[0];
  if (typeof first !== "string") return children;
  const m = STATUS_AT_START.exec(first.trimStart());
  if (!m) return children;
  const rest = first.trimStart().slice(m[0].length);
  return <span className="inline-flex items-start gap-1.5"><StatusIcon mark={m[1]} /><span>{rest}{items.slice(1)}</span></span>;
}

type El<T extends keyof JSX.IntrinsicElements> = ComponentProps<T> & { node?: unknown };

const ELEMENTS = {
  pre: ({ children, node: _node, ...rest }: El<"pre">) => {
    const child = Array.isArray(children) ? children[0] : children;
    if (isValidElement<{ className?: string; children?: ReactNode }>(child) && /\blanguage-chart\b/.test(child.props.className ?? "")) {
      const spec = parseChart(String(child.props.children ?? ""));
      return spec ? <AssessmentChart spec={spec} /> : null;
    }
    return <pre {...rest}>{children}</pre>;
  },
  td: ({ children, node: _node, ...rest }: El<"td">) => <td {...rest}>{withStatus(children)}</td>,
  li: ({ children, node: _node, ...rest }: El<"li">) => <li {...rest}>{withStatus(children)}</li>,
};
